import type { DailyResetService } from "@yjsgames/daily-reset";
import type { BigNumber } from "@yjsgames/bignumber";
import type { DomainControllers } from "../app/createControllers.js";
import type { ProgressState } from "../app/saveSwap.js";
import type { ShellSystems } from "../app/createShellSystems.js";
import type { TabRouter } from "../app/TabRouter.js";
import type { TabRefresher } from "../ui/TabRefresher.js";
import type { ShellFrame } from "../ui/ShellFrame.js";
import type { TabId } from "../ui/tabVisibility.js";
import { isTabLocked, tabUnlockChapter } from "../ui/tabLock.js";
import { chapterLockedTabs, shouldShowUnlockDot } from "../ui/tabUnlockDot.js";
import { hasNewlyUnlockedPiece } from "../ui/codexRedDot.js";
import { buildDailyShopData } from "../ui/tabData.js";
import type { DailyShopTabData } from "../ui/DailyShopTabView.js";
import { hasSelectableModuleMasteryBranch } from "../ui/moduleMasteryTabData.js";
import { bestChapterInCurrentWorld, chapterInWorld, worldForChapter } from "../data/worldData.js";
import { spaceshipById } from "../data/spaceshipData.js";
import { buildGuideQuestPanelData } from "../ui/guideQuestPanelData.js";
import { diag } from "../app/diag.js";

const guideQuestLog = diag("guideQuest");
let lastGuideQuestPanelDiagKey = "";

function guideQuestUrlDiagEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).has("diagGuideQuest");
}

export interface GameSceneRefreshDeps {
  readonly readiness: { readonly canRefresh: boolean };
  readonly modals: { isAnyModalOpen(): boolean };
  readonly tabRouter: Pick<TabRouter, "current" | "isBattle">;
  readonly tabs: Pick<TabRefresher, "refresh" | "refreshAll">;
  readonly frame: Pick<ShellFrame, "bottomTab" | "hud" | "updateGuideQuestPanel" | "setWorldVisual" | "setPlayerShipTexture">;
  readonly controllers: Pick<
    DomainControllers,
    "chapter" | "dailyShop" | "economy" | "upgradeInv" | "guideQuest" | "moduleMastery" | "spaceship"
  >;
  readonly progress: Pick<
    ProgressState,
    "adFree" | "codexSeenChapter" | "shopDailyAdDiamondClaimed" | "tabUnlockSeen"
  >;
  readonly shell: Pick<ShellSystems, "combat" | "dailyShop">;
  readonly dailyReset: DailyResetService;
  readonly codexUnlockChapters: readonly number[];
}

export function refreshAllViews(deps: GameSceneRefreshDeps): void {
  refreshHud(deps);
  refreshGuideQuestPanel(deps);
  refreshTabRedDots(deps);
  deps.tabs.refreshAll();
}

export function refreshActiveTab(deps: GameSceneRefreshDeps): void {
  if (!deps.readiness.canRefresh) return;
  refreshGuideQuestPanel(deps);
  if (deps.modals.isAnyModalOpen()) return;
  if (deps.tabRouter.isBattle()) return;
  deps.tabs.refresh(deps.tabRouter.current());
}

export function refreshOnTabEnter(deps: GameSceneRefreshDeps): void {
  if (!deps.readiness.canRefresh) return;
  refreshGuideQuestPanel(deps);
  deps.tabs.refresh(deps.tabRouter.current());
}

export function refreshTabLocks(deps: GameSceneRefreshDeps): void {
  const best = deps.controllers.chapter.bestChapter;
  deps.frame.bottomTab.setLocked((tab: TabId) =>
    isTabLocked(tab, best) ? tabUnlockChapter(tab) : null,
  );
  refreshTabRedDots(deps);
}

export function refreshTabRedDots(deps: GameSceneRefreshDeps): void {
  const best = deps.controllers.chapter.bestChapter;
  for (const tab of chapterLockedTabs()) {
    let show = shouldShowUnlockDot(tab, best, deps.progress.tabUnlockSeen);
    if (tab === "codex" && !isTabLocked(tab, best)) {
      show =
        show ||
        hasNewlyUnlockedPiece(
          deps.codexUnlockChapters,
          deps.progress.codexSeenChapter,
          best,
        );
    }
    if (tab === "moduleMastery") {
      show =
        show ||
        (!isTabLocked(tab, best) &&
          hasSelectableModuleMasteryBranch(deps.controllers.moduleMastery));
    }
    deps.frame.bottomTab.setRedDot(tab, show);
  }
}

export function buildGameSceneDailyShopData(deps: GameSceneRefreshDeps): DailyShopTabData {
  return buildDailyShopData(
    deps.controllers.dailyShop,
    deps.dailyReset,
    deps.controllers.economy,
    deps.controllers.chapter,
    deps.shell.dailyShop.lastResultText(),
    deps.progress.adFree,
    deps.shell.dailyShop.adProcessing(),
  );
}

export function refreshHud(deps: GameSceneRefreshDeps): void {
  const chapter = deps.controllers.chapter.currentChapter;
  const world = worldForChapter(chapter);
  const spaceship = spaceshipById(deps.controllers.spaceship.selectedId);
  deps.frame.setWorldVisual({ bgTileKey: world.bgTileKey });
  deps.frame.setPlayerShipTexture(spaceship?.smallAssetKey ?? "spaceship_launcher", spaceship?.shipScale ?? 1);
  deps.frame.hud.update({
    gold: deps.controllers.economy.gold as BigNumber,
    diamond: deps.controllers.economy.diamond as BigNumber,
    chapter: chapterInWorld(chapter),
    bestChapter: bestChapterInCurrentWorld(chapter, deps.controllers.chapter.bestChapter),
    worldNo: world.worldNo,
    worldIconKey: world.iconKey,
    upgradeStone: deps.controllers.upgradeInv.universalUpgradeStone,
    baseDamage: deps.shell.combat.baseDamage(),
  });
}

export function refreshGuideQuestPanel(deps: GameSceneRefreshDeps): void {
  const active = deps.controllers.guideQuest.active();
  const data = buildGuideQuestPanelData(active);
  deps.frame.updateGuideQuestPanel(data);
  if (guideQuestLog.isOn("diag") || guideQuestUrlDiagEnabled()) {
    const key = [
      active?.definition.questId ?? "",
      active?.progress ?? 0,
      active?.targetCount ?? 0,
      active?.completed ?? false,
      active?.claimed ?? false,
      data.visible,
      data.allDone,
    ].join("|");
    if (key !== lastGuideQuestPanelDiagKey) {
      lastGuideQuestPanelDiagKey = key;
      const payload = {
        questId: active?.definition.questId ?? "",
        progress: active?.progress ?? 0,
        target: active?.targetCount ?? 0,
        completed: active?.completed ?? false,
        claimed: active?.claimed ?? false,
        visible: data.visible,
        allDone: data.allDone,
      };
      if (guideQuestUrlDiagEnabled()) console.warn("[guideQuest][D][refreshPanel]", JSON.stringify(payload));
      else guideQuestLog.diag("refreshPanel", payload);
    }
  }
}
